import { Graph, Edge } from 'graphlib';
import { IAtom, IDataInstance } from '../data-instance/interfaces';
import { type PositionalConstraintError, type GroupOverlapError, type HiddenNodeConflictError, type IConstraintValidator, isPositionalConstraintError, isGroupOverlapError, isHiddenNodeConflictError } from './constraint-types';
import { EdgeStyle, normalizeEdgeStyle } from './edge-style';
import { resolveIconPath } from './icon-registry';
import type { SelectorErrorDetail } from '../components/ErrorMessageModal/ErrorStateManager';


import {
    LayoutNode, LayoutEdge, LayoutConstraint, InstanceLayout,
    LeftConstraint, TopConstraint, AlignmentConstraint, LayoutGroup,
    ImplicitConstraint, DisjunctiveConstraint, isLeftConstraint, isTopConstraint, isAlignmentConstraint,
    negateDisjunction
} from './interfaces';

import {
    LayoutSpec,
    RelativeOrientationConstraint, CyclicOrientationConstraint,
    RelativeDirection,
    GroupByField, GroupBySelector, AlignConstraint,
    EdgeColorDirective, InferredEdgeDirective, TagDirective,
    AtomHidingDirective
} from './layoutspec';


import IEvaluator from '../evaluators/interfaces';
import { SelectorArityError } from '../evaluators/interfaces';
import type { IEvaluatorResult } from '../evaluators/interfaces';
import { ColorPicker } from './colorpicker';
import { type ConstraintError, type ErrorMessages, ConstraintValidator, orientationConstraintToString } from './constraint-validator';
import { QualitativeConstraintValidator } from './qualitative-constraint-validator';
import { estimateLabelBox } from './text-extent';

/** The strings the renderer will draw inside a node's box. Used to size the box. */
type NodeDisplayContent = {
    label: string;
    /** One per attribute key, formatted "key: v1, v2, ..." to match the renderer. */
    attributeLines: string[];
    /** One per Skolem-label key, formatted as "v1, v2, ..." (key not shown by renderer). */
    skolemLines: string[];
};
const UNIVERSAL_TYPE = "univ";

type ConstraintSource = RelativeOrientationConstraint | CyclicOrientationConstraint | AlignConstraint | ImplicitConstraint;

/**
 * Result of layout generation. When constraints are unsatisfiable, `layout`
 * contains a counterfactual diagram built from the maximal feasible subset
 * of constraints, annotated with error metadata (conflictingConstraints,
 * overlappingNodes) for visual highlighting. `error` is non-null on conflict.
 */
type CounterfactualLayoutResult = {
    layout: InstanceLayout;
    error: ConstraintError | null;
    selectorErrors: SelectorErrorDetail[];
    /** The qualitative constraint validator, if used. Exposes modal spatial queries (must/can/cannot). */
    validator?: QualitativeConstraintValidator;
};

class MissingNodeConstraintError extends Error implements ConstraintError {
    readonly type = 'unknown-constraint';
    readonly missingNodeId: string;
    readonly sourceConstraint?: ConstraintSource;

    constructor(missingNodeId: string, sourceConstraint?: ConstraintSource) {
        const selector = sourceConstraint && 'selector' in sourceConstraint ? sourceConstraint.selector : undefined;
        const selectorDetails = selector ? ` Selector: ${selector}.` : '';
        super(`Constraint references node "${missingNodeId}" that is missing from the layout (it may be hidden).${selectorDetails}`);
        this.name = 'MissingNodeConstraintError';
        this.missingNodeId = missingNodeId;
        this.sourceConstraint = sourceConstraint;
        Object.setPrototypeOf(this, MissingNodeConstraintError.prototype);
    }
}

function isMissingNodeConstraintError(error: unknown): error is MissingNodeConstraintError {
    return error instanceof MissingNodeConstraintError;
}

/**
 * Strategy for adding alignment edges to prevent WebCola from falling into bad local minima.
 * 
 * - `never`: Never add alignment edges (maximum performance, may result in suboptimal layouts)
 * - `direct`: Only add alignment edges when nodes have no direct edge between them
 * - `connected`: Only add alignment edges when nodes are not connected via any path (default, best balance)
 */
export enum AlignmentEdgeStrategy {
    /** Never add alignment edges - maximum performance but may result in poor layouts */
    NEVER = 'never',
    /** Add alignment edges only when no direct edge exists between nodes */
    DIRECT = 'direct',
    /** Add alignment edges only when nodes are not connected via any path (default) */
    CONNECTED = 'connected'
}

/**
 * Strategy for which constraint validator to use.
 *
 * - `qualitative` (default): CDCL-based validator that reasons over qualitative
 *   partial-order constraints before handing off to Kiwi for coordinate
 *   assignment. Faster on large/group-heavy instances with geometry-aware
 *   pruning and clause learning.
 *
 * - `kiwi` (deprecated): The original Kiwi-based backtracking validator.
 *   Retained for backward compatibility and equivalence testing.
 */
export enum ConstraintValidatorStrategy {
    /** @deprecated Use QUALITATIVE instead. Will be removed in a future release. */
    KIWI = 'kiwi',
    /** CDCL-based qualitative validator (default) */
    QUALITATIVE = 'qualitative'
}


// Should create a NEW list when it returns.
function removeDuplicateConstraints(constraints: LayoutConstraint[]): LayoutConstraint[] {
    const uniqueConstraints: LayoutConstraint[] = [];
    const seen = new Set<string>();
    
    for (const constraint of constraints) {
        let key: string;
        
        if (isLeftConstraint(constraint)) {
            // For left constraints: left_node_id|right_node_id|minDistance
            key = `left|${constraint.left.id}|${constraint.right.id}|${constraint.minDistance}`;
        } else if (isTopConstraint(constraint)) {
            // For top constraints: top_node_id|bottom_node_id|minDistance
            key = `top|${constraint.top.id}|${constraint.bottom.id}|${constraint.minDistance}`;
        } else if (isAlignmentConstraint(constraint)) {
            // For alignment constraints: axis|node1_id|node2_id (order normalized)
            const [node1, node2] = [constraint.node1.id, constraint.node2.id].sort();
            key = `align|${constraint.axis}|${node1}|${node2}`;
        } else {
            // Fallback for unknown constraint types - include all in case they're different
            key = `unknown|${JSON.stringify(constraint)}`;
        }
        
        if (!seen.has(key)) {
            seen.add(key);
            uniqueConstraints.push(constraint);
        }
    }
    
    return uniqueConstraints;
}

/**
 * Produces a natural-language description for a dropped orientation constraint
 * involving a hidden node, matching the style of `orientationConstraintToString`.
 */
function describeDroppedOrientationConstraint(sourceNodeId: string, targetNodeId: string, direction: string): string {
    switch (direction) {
        case 'left':
            return `${sourceNodeId} must be to the left of ${targetNodeId}`;
        case 'right':
            return `${sourceNodeId} must be to the right of ${targetNodeId}`;
        case 'above':
            return `${sourceNodeId} must be above ${targetNodeId}`;
        case 'below':
            return `${sourceNodeId} must be below ${targetNodeId}`;
        case 'directlyLeft':
            return `${sourceNodeId} must be directly to the left of ${targetNodeId}`;
        case 'directlyRight':
            return `${sourceNodeId} must be directly to the right of ${targetNodeId}`;
        case 'directlyAbove':
            return `${sourceNodeId} must be directly above ${targetNodeId}`;
        case 'directlyBelow':
            return `${sourceNodeId} must be directly below ${targetNodeId}`;
        default:
            return `${sourceNodeId} is constrained relative to ${targetNodeId} (${direction})`;
    }
}

class LayoutNodePath {
    constructor(
        public Path: LayoutNode[],
        public LoopsTo: LayoutNode | undefined
    ) { }

    /**
     * Expands the path by unrolling the loop `repeat` times.
     * If there is no loop, returns the plain path.
     */
    expand(repeat: number): string[] {
        const ids = this.Path.map(n => n.id);

        if (!this.LoopsTo) return ids;

        const loopStart = ids.findIndex(id => id === this.LoopsTo!.id);
        if (loopStart === -1) return ids;

        const prefix = ids.slice(0, loopStart);
        const loop = ids.slice(loopStart);
        return prefix.concat(...Array(repeat).fill(loop));
    }

    /**
     * Returns true if `this` is a superpath that contains `other` as a subpath (after unrolling).
     */
    isSubpathOf(other: LayoutNodePath): boolean {
        const thisUnrolled = this.expand(2);
        const otherUnrolled = other.expand(1);

        if (otherUnrolled.length > thisUnrolled.length) return false;

        for (let i = 0; i <= thisUnrolled.length - otherUnrolled.length; i++) {
            if (otherUnrolled.every((v, j) => v === thisUnrolled[i + j])) {
                return true;
            }
        }
        return false;
    }

    /**
     * Returns true if two paths are equivalent (each is a subpath of the other).
     */
    static areEquivalent(p1: LayoutNodePath, p2: LayoutNodePath): boolean {
        return p1.isSubpathOf(p2) && p2.isSubpathOf(p1);
    }
}


export class LayoutInstance {


    readonly hideThisEdge = "_h_"
    static DISCONNECTED_PREFIX = "_d_"


    readonly DEFAULT_NODE_ICON_PATH: string = '';
    readonly DEFAULT_NODE_HEIGHT = 60;
    readonly DEFAULT_NODE_WIDTH = 100;

    private readonly _layoutSpec: LayoutSpec;

    public readonly minSepHeight = 15;
    public readonly minSepWidth = 15;

    private evaluator: IEvaluator;
    private instanceNum: number;

    private readonly alignmentEdgeStrategy: AlignmentEdgeStrategy;
    private readonly validatorStrategy: ConstraintValidatorStrategy;

    /**
     * Collector for selector errors encountered during layout generation.
     * Reset at the start of each generateLayout() call.
     */
    private selectorErrors: SelectorErrorDetail[] = [];

    /**
     * Map of hidden node IDs to the hideAtom selector that caused them to be hidden.
     * Populated by ensureNoExtraNodes() and used by constraint generation to detect conflicts.
     * Reset at the start of each generateLayout() call.
     */
    private hiddenNodeSelectors: Map<string, string> = new Map();

    /**
     * Tracks constraints that were dropped because they reference hidden nodes.
     * Each entry maps a source constraint description (HTML) to a list of
     * pairwise constraint descriptions that were skipped.
     * Reset at the start of each generateLayout() call.
     */
    private hiddenNodeConflicts: Map<string, string[]> = new Map();

    /**
     * Set of hidden node IDs that are actually involved in conflicts (i.e., referenced
     * by at least one dropped constraint). Used to build hideAtom table entries without
     * parsing description strings.
     * Reset at the start of each generateLayout() call.
     */
    private conflictedHiddenNodes: Set<string> = new Set();

    /**
     * Records a selector evaluation error for later reporting.
     * @param selector - The selector expression that failed
     * @param context - Description of where/why the selector was being evaluated
     * @param error - The original error that was caught
     */
    private recordSelectorError(selector: string, context: string, error: unknown): void {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.selectorErrors.push({
            selector,
            context,
            errorMessage
        });
    }

    /**
     * Checks whether the selector result has the expected arity.
     * Records a SelectorArityError and returns false if mismatched.
     * @param selectorRes - The evaluated selector result
     * @param selector - The selector expression (for error messages)
     * @param expectedArity - 'unary' or 'binary'
     * @param context - Description of where the selector is being used (e.g. 'orientation selector')
     * @returns true if arity matches (or result is empty), false if mismatched
     */
    private checkSelectorArity(
        selectorRes: IEvaluatorResult,
        selector: string,
        expectedArity: 'unary' | 'binary',
        context: string
    ): boolean {
        const arity = selectorRes.maxArity();
        if (arity === 0) {
            // No results — nothing to validate
            return true;
        }
        if (expectedArity === 'binary' && arity < 2) {
            this.recordSelectorError(
                selector,
                context,
                new SelectorArityError(selector, 'binary', 'unary')
            );
            return false;
        }
        if (expectedArity === 'unary' && arity > 1) {
            this.recordSelectorError(
                selector,
                context,
                new SelectorArityError(selector, 'unary', 'binary')
            );
            return false;
        }
        return true;
    }

    /**
     * Checks if a node is hidden by a hideAtom directive.
     * @param nodeId - The node ID to check
     * @returns true if the node was hidden by a hideAtom selector
     */
    private isNodeHiddenByDirective(nodeId: string): boolean {
        return this.hiddenNodeSelectors.has(nodeId);
    }

    /**
     * Records a conflict where a constraint references a hidden node.
     * The constraint is dropped from the layout, and the conflict is tracked for error reporting.
     * @param sourceConstraint - The source constraint that references the hidden node
     * @param pairwiseDescription - A human-readable description of the dropped pairwise constraint
     * @param hiddenNodeId - The ID of the hidden node
     */
    private recordHiddenNodeConflict(sourceConstraint: ConstraintSource, pairwiseDescription: string, hiddenNodeId: string): void {
        const sourceHTML = sourceConstraint.toHTML();
        if (!this.hiddenNodeConflicts.has(sourceHTML)) {
            this.hiddenNodeConflicts.set(sourceHTML, []);
        }
        this.hiddenNodeConflicts.get(sourceHTML)!.push(pairwiseDescription);

        // Track which hidden nodes are involved in conflicts (for building hideAtom table entries)
        this.conflictedHiddenNodes.add(hiddenNodeId);
    }


    /**
     * Constructs a new `LayoutInstance` object.
     *
     * @param layoutSpec - The layout specification that defines constraints, directives, and other layout-related configurations.
     * @param evaluator - An evaluator instance used to evaluate selectors and constraints within the layout specification.
     * @param instNum - The instance number (default is 0), used to differentiate between multiple instances of the same layout.
     * @param addAlignmentEdges - Deprecated. Use alignmentEdgeStrategy instead. A boolean flag indicating whether alignment edges should be added (default is `true`, equivalent to 'connected' strategy).
     * @param alignmentEdgeStrategy - Strategy for adding alignment edges (default is `AlignmentEdgeStrategy.CONNECTED`). Takes precedence over addAlignmentEdges if provided.
     * @param validatorStrategy - Which constraint validator to use (default is `ConstraintValidatorStrategy.QUALITATIVE`). Set to `KIWI` to use the deprecated Cassowary-based validator.
     *
     * The `LayoutInstance` class is responsible for generating a layout for a given data instance based on the provided layout specification.
     * It applies constraints, directives, and projections to produce a structured layout that can be rendered using a graph visualization library.
     */
    constructor(
        layoutSpec: LayoutSpec,
        evaluator: IEvaluator,
        instNum: number = 0,
        addAlignmentEdges: boolean = true,
        alignmentEdgeStrategy?: AlignmentEdgeStrategy,
        validatorStrategy?: ConstraintValidatorStrategy
    ) {
        this.instanceNum = instNum;
        this.evaluator = evaluator;
        this._layoutSpec = layoutSpec;

        // Handle backward compatibility: if alignmentEdgeStrategy is provided, use it
        // Otherwise, convert boolean addAlignmentEdges to strategy
        if (alignmentEdgeStrategy !== undefined) {
            this.alignmentEdgeStrategy = alignmentEdgeStrategy;
        } else {
            this.alignmentEdgeStrategy = addAlignmentEdges
                ? AlignmentEdgeStrategy.CONNECTED
                : AlignmentEdgeStrategy.NEVER;
        }

        this.validatorStrategy = validatorStrategy ?? ConstraintValidatorStrategy.QUALITATIVE;
    }

    get hideDisconnected(): boolean {
        return this._layoutSpec.directives.hideDisconnected || false;
    }

    get hideDisconnectedBuiltIns(): boolean {
        return this._layoutSpec.directives.hideDisconnectedBuiltIns || false;
    }



    /**
     * Gets GroupByField constraints that apply to a specific field and atoms.
     * @param fieldName - The field name to match.
     * @param sourceAtom - The source atom ID.
     * @param targetAtom - The target atom ID.
     * @returns Array of matching GroupByField constraints.
     */
    private getConstraintsRelatedToField(fieldName: string, sourceAtom: string, targetAtom: string): GroupByField[] {
        const groupByFieldConstraints = this._layoutSpec.constraints.grouping.byfield;
        
        let fieldConstraints = groupByFieldConstraints.filter((d) => {
            if (d.field !== fieldName) {
                return false;
            }
            
            if (!d.selector) {
                // Legacy constraint without selector applies to all edges with this field
                return true;
            }
            
            try {
                const selectorResult = this.evaluator.evaluate(d.selector, { instanceIndex: this.instanceNum });
                const selectedAtoms = selectorResult.selectedAtoms();
                
                // Check if source atom is selected by the selector
                return selectedAtoms.includes(sourceAtom);
            } catch (error) {
                this.recordSelectorError(d.selector, 'group by field selector', error);
                return false;
            }
        });
        return fieldConstraints;
    }

    isAttributeField(fieldId: string, sourceAtom?: string, targetAtom?: string): boolean {
        const matchingDirectives = this._layoutSpec.directives.attributes.filter((ad) => ad.field === fieldId);
        
        if (matchingDirectives.length === 0) {
            return false;
        }
        
        // If no atoms provided, use legacy behavior for directives without selector/filter
        if (!sourceAtom || !targetAtom) {
            return matchingDirectives.some(ad => !ad.selector && !ad.filter);
        }
        
        // Check selector-based and filter-based directives
        for (const directive of matchingDirectives) {
            // Check if selector matches (or no selector means match all sources)
            let selectorMatches = true;
            if (directive.selector) {
                try {
                    const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                    const selectedAtoms = selectorResult.selectedAtoms();
                    selectorMatches = selectedAtoms.includes(sourceAtom);
                } catch (error) {
                    this.recordSelectorError(directive.selector, 'attribute selector', error);
                    selectorMatches = false;
                }
            }
            
            if (!selectorMatches) {
                continue;
            }
            
            // Check if filter matches (or no filter means match all tuples)
            let filterMatches = true;
            if (directive.filter) {
                try {
                    const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                    const selectedTuples = filterResult.selectedTwoples();
                    // Check if the (source, target) pair is in the filtered set
                    filterMatches = selectedTuples.some(
                        tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                    );
                } catch (error) {
                    this.recordSelectorError(directive.filter, 'attribute filter', error);
                    filterMatches = false;
                }
            }
            
            if (selectorMatches && filterMatches) {
                return true;
            }
        }
        
        return false;
    }

    isHiddenField(fieldId: string, sourceAtom?: string, targetAtom?: string): boolean {
        // First check hiddenFields directives
        const matchingDirectives = this._layoutSpec.directives.hiddenFields.filter((hd) => hd.field === fieldId);
        
        if (matchingDirectives.length > 0) {
            // If no atoms provided, use legacy behavior for directives without selector/filter
            if (!sourceAtom || !targetAtom) {
                if (matchingDirectives.some(hd => !hd.selector && !hd.filter)) {
                    return true;
                }
            } else {
                // Check selector-based and filter-based directives
                for (const directive of matchingDirectives) {
                    // Check if selector matches (or no selector means match all sources)
                    let selectorMatches = true;
                    if (directive.selector) {
                        try {
                            const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                            const selectedAtoms = selectorResult.selectedAtoms();
                            selectorMatches = selectedAtoms.includes(sourceAtom);
                        } catch (error) {
                            this.recordSelectorError(directive.selector, 'hidden field selector', error);
                            selectorMatches = false;
                        }
                    }
                    
                    if (!selectorMatches) {
                        continue;
                    }
                    
                    // Check if filter matches (or no filter means match all tuples)
                    let filterMatches = true;
                    if (directive.filter) {
                        try {
                            const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                            const selectedTuples = filterResult.selectedTwoples();
                            // Check if the (source, target) pair is in the filtered set
                            filterMatches = selectedTuples.some(
                                tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                            );
                        } catch (error) {
                            this.recordSelectorError(directive.filter, 'hidden field filter', error);
                            filterMatches = false;
                        }
                    }
                    
                    if (selectorMatches && filterMatches) {
                        return true;
                    }
                }
            }
        }
        
        // Also check EdgeStyle directives with hidden: true
        if (sourceAtom && targetAtom) {
            const edgeDirective = this.findEdgeDirective(fieldId, sourceAtom, targetAtom);
            if (edgeDirective?.hidden === true) {
                return true;
            }
        }
        
        return false;
    }


    /**
     * Generates groups based on the specified graph.
     * @param g - The graph, which will be modified to remove the edges that are used to generate groups.
     * @param a - The ORIGINAL (pre-projection) Data Instance.
     * @returns A record of groups.
     */
    private normalizeComparableGroupToken(token: string): string {
        const trimmed = token.trim();
        if (
            (trimmed.startsWith('"') && trimmed.endsWith('"'))
            || (trimmed.startsWith("'") && trimmed.endsWith("'"))
        ) {
            return trimmed.slice(1, -1);
        }
        return trimmed;
    }

    private buildGroupKeyDisplay(nodeId: string, nodeLabel?: string): string {
        const normalizedId = this.normalizeComparableGroupToken(nodeId);
        const rawLabel = (nodeLabel || '').trim();

        if (!rawLabel) {
            return normalizedId || nodeId;
        }

        const normalizedLabel = this.normalizeComparableGroupToken(rawLabel);

        // Avoid redundant keys like x1:"x1" when they represent the same value.
        if (normalizedLabel === normalizedId) {
            return normalizedId || normalizedLabel || nodeId;
        }

        return `${rawLabel}:${nodeId}`;
    }

    private generateGroups(g: Graph, a: IDataInstance): LayoutGroup[] {

        //let groupingConstraints : GroupingConstraint[] = this._layoutSpec.constraints.grouping;


        let groupByFieldConstraints: GroupByField[] = this._layoutSpec.constraints.grouping.byfield;
        let groupBySelectorConstraints: GroupBySelector[] = this._layoutSpec.constraints.grouping.byselector;


        if (!groupByFieldConstraints && !groupBySelectorConstraints) {
            return [];
        }

        let groups: LayoutGroup[] = [];

        // First we go through the group by selector constraints.
        for (var gc of groupBySelectorConstraints) {

            let selector = gc.selector;
            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(selector, 'groupBySelector selector', error);
                continue; // Skip this group constraint
            }


            // Now, we should support both unary and binary selectors.

            // Use maxArity to decide which path to take, avoiding SelectorArityError
            // since groups intentionally support both unary and binary selectors.
            let isBinary = selectorRes.maxArity() > 1;
            
            if (isBinary) {
                let selectedTwoples: string[][] = selectorRes.selectedTwoples();

                function constructGroupEdgeID(edgelabel: string, src : string, tgt: string): string {
                    return `_g_0_1_` + edgelabel + `:` + src + `->` + tgt;
                }

                // The first element of each tuple is the key (i.e. groupOn)
                // The second element is the element to add to the group (i.e. addToGroup)

                // The name of the group is the relation name ':' the key node.

                for (var t of selectedTwoples) {

                    // Here, it should be the ID and the label of the node.



                    let groupOn = t[0];
                    let addToGroup = t[1];

                    const keyNodeLabel = g.node(groupOn)?.label;
                    const groupOnLabel = this.buildGroupKeyDisplay(groupOn, keyNodeLabel);
                    let groupName = `${gc.name}[${groupOnLabel}]`;

                    // Check if the group already exists
                    let existingGroup: LayoutGroup | undefined = groups.find((group) => group.name === groupName);

                    if (existingGroup) {
                        existingGroup.nodeIds.push(addToGroup);
                    }
                    else {
                        let newGroup: LayoutGroup =
                        {
                            name: groupName,
                            nodeIds: [addToGroup],
                            keyNodeId: groupOn,
                            showLabel: !gc.negated,
                            sourceConstraint: gc,
                            negated: gc.negated
                        };
                        groups.push(newGroup);

                        // NOW if we have the GroupBySelector addEdge flag set, we should add an edge between the keyNode and the new node.
                        if(gc.addEdge) {

                            const edgeId = constructGroupEdgeID(groupName, groupOn, addToGroup);
                            g.setEdge(groupOn, addToGroup, groupName, edgeId);
                        }

                    }
                }




            }
            else {
                let selectedElements: string[] = selectorRes.selectedAtoms();

                // Nothing to do if there are no selected elements, or 
                // if things are typed weirdly.
                if (selectedElements.length === 0) {
                    continue;
                }

                let keyNode = selectedElements[0]; // TODO: WAIT, THERE IS NO KEY NODE

                // Question: Does **just** having LayoutGroup work? Like what does a keyNode even mean?
                let newGroup: LayoutGroup = {
                    name: gc.name,
                    nodeIds: selectedElements,
                    keyNodeId: keyNode, //// TODO: I think introducing this random keynode could be a problem. Not sure why or when though.
                    showLabel: !gc.negated,
                    sourceConstraint: gc,
                    negated: gc.negated
                };
                groups.push(newGroup);
            }
        }


        // Now we go through the group by field constraints.

        let graphEdges = [...g.edges()];


        graphEdges.forEach((edge) => {
            const edgeId = edge.name;
            const relName = this.getRelationName(g, edge);


            let relatedConstraints = this.getConstraintsRelatedToField(relName, edge.v, edge.w);

            if (relatedConstraints.length === 0) {
                return;
            }

            // let edgeLabel = this.getEdgeLabel(g, edge); // Unused for now


            relatedConstraints.forEach((c) => {

                const groupOn = c.groupOn; // This is the part of the relation tuple that is the key.
                const addToGroup = c.addToGroup; // This is the part of the relation tuple that is IN the group.


                const potentialTuples = this.getFieldTuplesForSourceAndTarget(a, relName, edge.v, edge.w);
                if (!potentialTuples || potentialTuples.length === 0) {
                    return;
                }


                for (var thisTuple of potentialTuples) {
                    let arity = thisTuple?.length || 0;
                    if (arity < 2 || (groupOn < 0 || groupOn >= arity) || (addToGroup < 0 || addToGroup >= arity)) {
                        throw new Error(`Invalid grouping. groupOn=${groupOn} and addToGroup=${addToGroup} for ${arity}-ary relation ${relName}. These must be between 0 and ${arity - 1}.`);
                    }
                    // Now get the element of edge

                    // let sourceInGraph = thisTuple[0]; // Unused for now
                    // let targetInGraph = thisTuple[arity - 1]; // Unused for now

                    let key = thisTuple[groupOn];
                    let toAdd = thisTuple[addToGroup];


                    let labelString = thisTuple.map((s, idx) => {
                        if (idx === groupOn) {
                            return s;
                        }
                        else return "_";
                    }).join(",");

                    let groupName = `${relName}[${labelString}]`; // TODO: THis?

                    // Check if the group already exists
                    let existingGroup: LayoutGroup | undefined = groups.find((group) => group.name === groupName);

                    if (existingGroup) {
                        existingGroup.nodeIds.push(toAdd);
                        // But also remove this edge from the graph.
                        g.removeEdge(edge.v, edge.w, edgeId);
                    }
                    else {

                        let newGroup: LayoutGroup =
                        {
                            name: groupName,
                            nodeIds: [toAdd],
                            keyNodeId: key, // What if the key is in the graph?
                            showLabel: !c.negated,
                            sourceConstraint: c,
                            negated: c.negated
                        };
                        groups.push(newGroup);

                        const groupEdgePrefix = `_g_${groupOn}_${addToGroup}_`;
                        const newId = groupEdgePrefix + edgeId;
                        g.removeEdge(edge.v, edge.w, edgeId);
                        g.setEdge(edge.v, edge.w, groupName, newId);
                    }
                }
            });
        });




        
        return groups;
    }

    /**
     * Generates groups based on the specified graph.
     * @param g - The graph, which will be modified to remove the edges that are used to determine attributes.
     * @returns A record of attributes
     */
    private generateAttributesAndRemoveEdges(g: Graph): Record<string, Record<string, string[]>> {
        // Node : [] of attributes
        let attributes: Record<string, Record<string, string[]>> = {};

        let graphEdges = [...g.edges()];
        // Go through all edge labels in the graph

        graphEdges.forEach((edge) => {
            const edgeId = edge.name;
            const relName = this.getRelationName(g, edge);
            const sourceAtom = edge.v;
            const targetAtom = edge.w;
            const isAttributeRel = this.isAttributeField(relName, sourceAtom, targetAtom);
            const isHiddenRel = this.isHiddenField(relName, sourceAtom, targetAtom);

            if (isHiddenRel && isAttributeRel) {
                throw new Error(`${relName} cannot be both an attribute and a hidden field.`);
            }

            if (isHiddenRel) {
                // If the field is a hidden field, we should remove the edge from the graph.
                g.removeEdge(edge.v, edge.w, edgeId);
                return;
            }

            if (isAttributeRel) {

                // If the field is an attribute field, we should add the attribute to the source node's
                // attributes field.

                const attributeKey = this.getEdgeLabel(g, edge);
                let source = edge.v;
                let target = edge.w;

                // Really, we should be pushing the target node's LABEL.
                let targetLabel = g.node(target)?.label || target; // Use the node's label or the node ID if no label exists.

                let nodeAttributes = attributes[source] || {};

                if (!nodeAttributes[attributeKey]) {
                    nodeAttributes[attributeKey] = [];
                    attributes[source] = nodeAttributes;
                }
                nodeAttributes[attributeKey].push(targetLabel);

                // Now remove the edge from the graph
                g.removeEdge(edge.v, edge.w, edgeId);
            }
        });

        return attributes;
    }

    /**
     * Generates tag-based attributes for nodes based on TagDirectives.
     * Unlike attributes which come from edges, tags are computed from n-ary selectors
     * and added to nodes selected by the toTag selector.
     * 
     * For n-ary selector results (e.g., x1->y1->z1, x2->y2->z2), the format is:
     *   name[x1][y1]: z1
     *   name[x2][y2]: z2
     * 
     * For unary selector results (single atom), it's just:
     *   name: value
     * 
     * @param g - The graph to get node information from.
     * @param existingAttributes - Existing attributes to merge tags into.
     * @returns Updated attributes record with tags merged in.
     */
    private generateTagsForNodes(
        g: Graph,
        existingAttributes: Record<string, Record<string, string[]>>
    ): Record<string, Record<string, string[]>> {
        const tagDirectives = this._layoutSpec.directives.tags;
        
        if (!tagDirectives || tagDirectives.length === 0) {
            return existingAttributes;
        }

        const attributes = { ...existingAttributes };
        const graphNodes = new Set(g.nodes());

        for (const directive of tagDirectives) {
            try {
                // First, evaluate the toTag selector to get which nodes receive this tag
                const toTagResult = this.evaluator.evaluate(directive.toTag, { instanceIndex: this.instanceNum });
                const selectedAtoms = toTagResult.selectedAtoms();
                
                // Then, evaluate the value selector to get the n-ary result
                const valueResult = this.evaluator.evaluate(directive.value, { instanceIndex: this.instanceNum });
                const allTuples = valueResult.selectedTuplesAll();
                
                // For each node that should receive this tag
                for (const atomId of selectedAtoms) {
                    // Only add tags to nodes that exist in the graph
                    if (!graphNodes.has(atomId)) {
                        continue;
                    }
                    
                    // Ensure the node has an attributes object
                    if (!attributes[atomId]) {
                        attributes[atomId] = {};
                    }
                    
                    // Find tuples where the first element matches this atom
                    const matchingTuples = allTuples.filter(tuple => tuple[0] === atomId);
                    
                    if (matchingTuples.length === 0) {
                        // No matching tuples for this atom, skip
                        continue;
                    }
                    
                    for (const tuple of matchingTuples) {
                        if (tuple.length === 1) {
                            // Unary tuple: just name: value (the atom itself)
                            const attrKey = directive.name;
                            if (!attributes[atomId][attrKey]) {
                                attributes[atomId][attrKey] = [];
                            }
                            // For unary, the value is the atom label itself
                            const nodeLabel = g.node(atomId)?.label || atomId;
                            attributes[atomId][attrKey].push(String(nodeLabel));
                        } else if (tuple.length === 2) {
                            // Binary tuple: name: lastValue
                            const attrKey = directive.name;
                            if (!attributes[atomId][attrKey]) {
                                attributes[atomId][attrKey] = [];
                            }
                            const lastValue = tuple[tuple.length - 1];
                            // Get the label for the last element if it's a node
                            const valueLabel = graphNodes.has(String(lastValue)) 
                                ? (g.node(String(lastValue))?.label || String(lastValue))
                                : String(lastValue);
                            attributes[atomId][attrKey].push(valueLabel);
                        } else {
                            // N-ary tuple (n > 2): name[mid1][mid2]...: lastValue
                            // The key includes all middle elements
                            const middleElements = tuple.slice(1, -1);
                            const lastValue = tuple[tuple.length - 1];
                            
                            // Format middle elements with brackets
                            const middlePart = middleElements
                                .map(el => {
                                    const elStr = String(el);
                                    // Use node label if it's a graph node
                                    const label = graphNodes.has(elStr)
                                        ? (g.node(elStr)?.label || elStr)
                                        : elStr;
                                    return `[${label}]`;
                                })
                                .join('');
                            
                            const attrKey = `${directive.name}${middlePart}`;
                            if (!attributes[atomId][attrKey]) {
                                attributes[atomId][attrKey] = [];
                            }
                            
                            // Get the label for the last element if it's a node
                            const valueLabel = graphNodes.has(String(lastValue))
                                ? (g.node(String(lastValue))?.label || String(lastValue))
                                : String(lastValue);
                            attributes[atomId][attrKey].push(valueLabel);
                        }
                    }
                }
            } catch (error) {
                this.recordSelectorError(
                    `name: ${directive.name}, toTag: ${directive.toTag}, value: ${directive.value}`,
                    'tag directive',
                    error
                );
            }
        }

        return attributes;
    }

    /**
    * Modifies the graph to remove extraneous nodes (ex. those to be hidden)
    * Also populates this.hiddenNodeSelectors so constraint generation can detect conflicts.
    * @param g - The graph, which will be modified to remove extraneous nodes.
    */
    private ensureNoExtraNodes(g: Graph, a: IDataInstance) {

        let nodes = [...g.nodes()];

        // Pre-evaluate all hideAtom directives once (not per node)
        const hiddenAtomDirectives = this._layoutSpec.directives.hiddenAtoms;
        const evaluatedHideDirectives: { selector: string; hiddenSet: Set<string> }[] = [];
        for (const directive of hiddenAtomDirectives) {
            try {
                const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                if (!this.checkSelectorArity(selectorResult, directive.selector, 'unary', 'hideAtom selector')) {
                    continue; // Skip — binary selector in unary position (recorded once)
                }
                const selectedAtoms = selectorResult.selectedAtoms();
                evaluatedHideDirectives.push({
                    selector: directive.selector,
                    hiddenSet: new Set(selectedAtoms),
                });
            } catch (error) {
                this.recordSelectorError(directive.selector, 'hideAtom selector', error);
            }
        }

        nodes.forEach((node) => {


            // Check if builtin
            try {
                const type = a.getAtomType(node);
                const isAtomBuiltin = type?.isBuiltin || false;

                let inEdges = g.inEdges(node) || [];
                let outEdges = g.outEdges(node) || [];
                const isDisconnected = inEdges.length === 0 && outEdges.length === 0;


                // Legacy hiding logic for backwards compatibility
                const hideLegacy = isDisconnected && ((this.hideDisconnectedBuiltIns && isAtomBuiltin) || this.hideDisconnected);

                // New selector-based hiding logic
                let hideBySelector = false;
                let hidingSelector: string | undefined;
                for (const evaluated of evaluatedHideDirectives) {
                    if (evaluated.hiddenSet.has(node)) {
                        hideBySelector = true;
                        hidingSelector = evaluated.selector;
                        break;
                    }
                }

                const hideNode = hideLegacy || hideBySelector;

                if (hideNode) {
                    g.removeNode(node);
                    // Track selector-hidden nodes so constraint generation can report conflicts
                    if (hideBySelector && hidingSelector) {
                        this.hiddenNodeSelectors.set(node, hidingSelector);
                    }
                }

            } catch (error) {
                console.error("Failed to identify node type. Defaulting to showing node.", error);
            }
        });
    }


    private getMostSpecificType(node: string, a: IDataInstance): string {
        let allTypes = this.getNodeTypes(node, a);
        let mostSpecificType = allTypes[0];
        return mostSpecificType;
    }

    private getNodeTypes(node: string, a: IDataInstance): string[] {
        let type = a.getAtomType(node);
        let allTypes = type?.types || [];
        allTypes = allTypes.concat(UNIVERSAL_TYPE);
        return allTypes;
    }


    public getRelationName(g: Graph, edge: Edge): string {
        let relNameRaw = this.getEdgeLabel(g, edge);

        try {

            let relName = relNameRaw.split("[")[0];
            return relName;
        }
        catch {
            console.warn(`Failed to parse relation name from edge label: ${relNameRaw}. Defaulting to empty string.`);
            return relNameRaw;
        }
    }

    private getEdgeLabel(g: Graph, edge: Edge): string {
        return g.edge(edge.v, edge.w, edge.name);
    }

    /**
     * Generates the layout for the given data instance.
     * Projections should be applied to the data instance before calling this method
     * using `applyProjectionTransform()` from the data-instance module.
     * @param a - The data instance to generate the layout for.
     * @returns An object containing the layout, constraint error (if any), and any selector errors encountered.
     * @throws {ConstraintError} If the layout cannot be generated due to unsatisfiable constraints and error isn't caught to be surfaced to the user.
     */
    public generateLayout(
        a: IDataInstance
    ): CounterfactualLayoutResult {
        // Reset selector errors at the start of each layout generation
        this.selectorErrors = [];
        // Reset hidden-node tracking at the start of each layout generation
        this.hiddenNodeSelectors = new Map();
        this.hiddenNodeConflicts = new Map();
        this.conflictedHiddenNodes = new Set();

        let ai = a;

        // Do not apply hideDisconnectedBuiltIns here — inferred edges may connect built-in atoms.
        // We apply built-in hiding afterwards in `ensureNoExtraNodes` so inferred edges can reconnect them.
        let g: Graph = ai.generateGraph(this.hideDisconnected, false);

        let attributes = this.generateAttributesAndRemoveEdges(g);
        
        // Generate and merge tags into attributes
        attributes = this.generateTagsForNodes(g, attributes);

        // This is where we add the inferred edges to the graph.
        this.addinferredEdges(g);


        /// Groups have to happen here ///
        let groups = this.generateGroups(g, a);
        this.ensureNoExtraNodes(g, a);

        // Recompute visual maps after graph mutations (inferred edges / node removal)
        let nodeIconMap = this.getNodeIconMap(g);
        let nodeColorMap = this.getNodeColorMap(g, ai);

        // Compute the display strings once — single source of truth for "what's
        // drawn inside the box" so the box sizer (getNodeSizeMap) and the
        // LayoutNode mapping below see the same content.
        let contentByNode = this.getNodeDisplayContent(g, ai, attributes);
        let nodeSizeMap = this.getNodeSizeMap(g, contentByNode);

        let dcN = this.getDisconnectedNodes(g);



        /// NOW, we should get the nodes back with their IDs.



        let layoutNodes: LayoutNode[] = g.nodes().map((nodeId) => {

            let nodeMetadata = g.node(nodeId);
            // If the node has a label, we can use it.
            // Otherwise, we can use the nodeId as the label.
            let label = nodeMetadata?.label || nodeId;
            let color = nodeColorMap[nodeId] || "black";
            let iconDetails = nodeIconMap[nodeId];
            let iconPath = iconDetails.path;
            let showLabels = iconDetails.showLabels;

            let { height, width } = nodeSizeMap[nodeId];

            const mostSpecificType = this.getMostSpecificType(nodeId, a);
            const allTypes = this.getNodeTypes(nodeId, a);

            let nodeGroups = groups
                .filter((group) => group.nodeIds.includes(nodeId))
                .map((group) => group.name);
            let nodeAttributes = attributes[nodeId] || {};

            // Get labels from the data instance (e.g., Skolems in Alloy)
            let nodeLabels: Record<string, string[]> | undefined = undefined;
            const atoms = ai.getAtoms();
            const atom = atoms.find(atom => atom.id === nodeId);
            if (atom?.labels) {
                nodeLabels = atom.labels;
            }

            return {
                id: nodeId,
                label: label,
                name: label,
                color: color,
                groups: nodeGroups,
                attributes: nodeAttributes,
                labels: nodeLabels,
                icon: iconPath,
                height: height,
                width: width,
                mostSpecificType: mostSpecificType,
                types: allTypes,
                showLabels: showLabels,
                disconnected: dcN.includes(nodeId)
            };
        });

        ///////////// CONSTRAINTS ////////////


        let constraints: LayoutConstraint[] = [];
        let orientationConstraintCount = 0;
        let alignConstraintCount = 0;
        let layoutEdges: LayoutEdge[] = [];
        let cyclicDisjunctions: DisjunctiveConstraint[] = [];

        // Collect disjunctive constraints from negated alignment and cyclic
        let allDisjunctions: DisjunctiveConstraint[] = [];

        try {
            const orientationResult = this.applyRelativeOrientationConstraints(layoutNodes, g);
            constraints = orientationResult.conjunctive;
            allDisjunctions.push(...orientationResult.disjunctive);
            orientationConstraintCount = constraints.length;

            const alignResult = this.applyAlignConstraints(layoutNodes, g);
            constraints = constraints.concat(alignResult.conjunctive);
            allDisjunctions.push(...alignResult.disjunctive);
            alignConstraintCount = alignResult.conjunctive.length;

            console.log(`Generated ${orientationConstraintCount} orientation constraints and ${alignConstraintCount} alignment constraints (deduped + transitive reduction applied)`);

            // Prune redundant alignment edges after all have been added
            this.pruneRedundantAlignmentEdges(g);

            // Constraints NOW holds the conjuctive CORE of layout constraints.
            constraints = removeDuplicateConstraints(constraints);

            // Build cyclic constraint disjunctions (must happen before buildLayoutEdges
            // so that alignment edges added for cyclic pairs are included in the edge list)
            cyclicDisjunctions = this.buildCyclicDisjunctions(layoutNodes, g);
            allDisjunctions.push(...cyclicDisjunctions);

            layoutEdges = this.buildLayoutEdges(g, layoutNodes);
        } catch (error) {
            if (isMissingNodeConstraintError(error)) {
                return this.handleMissingNodeConstraintError(
                    error,
                    {
                        layoutNodes,
                        graph: g,
                        groups,
                        disconnectedNodes: dcN,
                        constraints
                    }
                );
            }
            throw error;
        }

        // Create layout with conjunctive constraints and disjunctive constraints
        let layout: InstanceLayout = {
            nodes: layoutNodes,
            edges: layoutEdges,
            constraints: constraints,
            groups: groups,
            disjunctiveConstraints: allDisjunctions.length > 0 ? allDisjunctions : undefined
        };

        // Check for hidden-node conflicts: constraints that were dropped because they
        // reference nodes hidden by hideAtom directives. Report these like IIS conflicts.
        if (this.hiddenNodeConflicts.size > 0) {
            return this.handleHiddenNodeConflictError(layout);
        }

        // Validate all constraints (conjunctive + disjunctive) in one pass
        const validator: IConstraintValidator = this.validatorStrategy === ConstraintValidatorStrategy.QUALITATIVE
            ? new QualitativeConstraintValidator(layout)
            : new ConstraintValidator(layout);
        const constraintError = validator.validateConstraints();

        if (constraintError) {
            if (isPositionalConstraintError(constraintError)) {
                return this.handlePositionalConstraintError(constraintError, layout);
            }

            if (isGroupOverlapError(constraintError)) {
                return this.handleGroupOverlapError(constraintError, layout);
            }

            throw constraintError;
        }

        // Update constraints with those added by the validator
        // (includes chosen alternatives from disjunctions and implicit alignment constraints)
        constraints = layout.constraints;

        // Filter out all edges that are hidden
        layoutEdges = this.filterHiddenEdges(layoutEdges);

        // Update the layout with final groups
        // Filter out negated groups — they don't produce visual rectangles;
        // their disjunctions were already added to the solver by the validators.
        layout.nodes = layoutNodes;
        layout.edges = layoutEdges;
        layout.constraints = constraints;
        layout.groups = groups.filter(g => !g.negated);

        // Return layout with selectorErrors (if any) - these don't block the layout
        // but callers should check and display them to the user
        const qualitativeValidator = validator instanceof QualitativeConstraintValidator ? validator : undefined;
        return { layout, error: null, selectorErrors: this.selectorErrors, validator: qualitativeValidator };
    }

    /**
     * Handles errors where a constraint references a node missing from the graph
     * (e.g., hidden by a hideAtom directive). Returns a counterfactual layout
     * built from the constraints that could be applied.
     */
    private handleMissingNodeConstraintError(
        error: MissingNodeConstraintError,
        context: {
            layoutNodes: LayoutNode[],
            graph: Graph,
            groups: LayoutGroup[],
            disconnectedNodes: string[],
            constraints: LayoutConstraint[],
        }
    ): CounterfactualLayoutResult {
        const layoutEdges = this.filterHiddenEdges(
            this.buildLayoutEdges(context.graph, context.layoutNodes)
        );
        const layoutGroups = context.groups;

        const counterfactualLayout: InstanceLayout = {
            nodes: context.layoutNodes,
            edges: layoutEdges,
            constraints: context.constraints,
            groups: layoutGroups,
            disjunctiveConstraints: []
        };

        return {
            layout: counterfactualLayout,
            error,
            selectorErrors: this.selectorErrors
        };
    }

    /**
     * Handles conflicts where hideAtom directives hide nodes referenced by layout
     * constraints. Reports the conflict in IIS-like table format and returns a
     * counterfactual layout with the conflicting constraints already dropped.
     */
    private handleHiddenNodeConflictError(
        layout: InstanceLayout
    ): CounterfactualLayoutResult {
        // Build the IIS-like error messages map:
        //   Source Constraint HTML → list of dropped pairwise constraint descriptions
        // Also add a synthetic entry for each hideAtom directive involved
        const sourceConstraintHTMLToLayoutConstraintsHTML = new Map<string, string[]>();

        // Collect all unique hideAtom selectors involved
        const involvedHideSelectors = new Set<string>();

        for (const [sourceHTML, descriptions] of this.hiddenNodeConflicts.entries()) {
            sourceConstraintHTMLToLayoutConstraintsHTML.set(sourceHTML, descriptions);
        }

        // Derive involved hideAtom selectors from conflictedHiddenNodes
        for (const nodeId of this.conflictedHiddenNodes) {
            const selector = this.hiddenNodeSelectors.get(nodeId);
            if (selector) {
                involvedHideSelectors.add(selector);
            }
        }

        // Add hideAtom directives as source constraints in the table
        for (const hideSelector of involvedHideSelectors) {
            const hideHTML = `hideAtom with selector <code>${hideSelector}</code>`;
            // Collect which nodes this selector hid that are involved in conflicts
            const hiddenNodeDescriptions: string[] = [];
            for (const [nodeId, selector] of this.hiddenNodeSelectors.entries()) {
                if (selector === hideSelector) {
                    hiddenNodeDescriptions.push(`${nodeId} is hidden`);
                }
            }
            if (hiddenNodeDescriptions.length > 0) {
                sourceConstraintHTMLToLayoutConstraintsHTML.set(hideHTML, hiddenNodeDescriptions);
            }
        }

        // Build a summary message
        const hiddenNodeIds = [...this.hiddenNodeSelectors.keys()];
        const firstConflictSourceHTML = [...this.hiddenNodeConflicts.keys()][0];
        const firstConflictDescription = this.hiddenNodeConflicts.get(firstConflictSourceHTML)?.[0] || '';

        const errorMessages: ErrorMessages = {
            conflictingConstraint: firstConflictDescription,
            conflictingSourceConstraint: firstConflictSourceHTML,
            minimalConflictingConstraints: sourceConstraintHTMLToLayoutConstraintsHTML
        };

        const error: HiddenNodeConflictError = {
            name: 'HiddenNodeConflictError',
            type: 'hidden-node-conflict',
            message: `Constraints reference hidden nodes [${hiddenNodeIds.join(', ')}]. These constraints have been dropped from the layout.`,
            hiddenNodes: this.hiddenNodeSelectors,
            droppedConstraints: this.hiddenNodeConflicts,
            errorMessages
        };

        // The layout already has the conflicting constraints dropped (they were skipped
        // during generation), so it serves as the counterfactual diagram.
        // Apply the same edge-visibility post-processing that the normal path performs.
        const counterfactualLayout: InstanceLayout = {
            ...layout,
            edges: this.filterHiddenEdges(layout.edges)
        };

        return {
            layout: counterfactualLayout,
            error,
            selectorErrors: this.selectorErrors
        };
    }

    /**
     * Handles positional constraint conflicts (unsatisfiable ordering/alignment).
     * Returns a counterfactual layout enforcing the maximal feasible subset of
     * constraints, with conflicting constraints annotated for visual highlighting.
     */
    private handlePositionalConstraintError(
        error: PositionalConstraintError,
        layout: InstanceLayout
    ): CounterfactualLayoutResult {
        const minimalConflictingSet = error.minimalConflictingSet;

        // The qualitative validator enforces the MFS on layout.constraints
        // before returning the error. Use the layout as-is in that case.
        // For the Kiwi validator (no MFS), fall back to removing the IIS.
        const constraints = error.maximalFeasibleSubset
            ? layout.constraints
            : layout.constraints.filter(c =>
                ![...minimalConflictingSet.values()].flat().includes(c)
            );

        const counterfactualLayout: InstanceLayout = {
            nodes: layout.nodes,
            edges: layout.edges,
            constraints,
            groups: layout.groups,
            conflictingConstraints: [...minimalConflictingSet.values()].flat()
        };
        return {
            layout: counterfactualLayout,
            error: error,
            selectorErrors: this.selectorErrors
        };
    }

    /**
     * Handles group overlap errors where nodes belong to multiple non-overlapping
     * groups. Returns a counterfactual layout scoped to the overlapping groups
     * and their nodes, with overlapping nodes annotated for visual highlighting.
     */
    private handleGroupOverlapError(
        error: GroupOverlapError,
        layout: InstanceLayout
    ): CounterfactualLayoutResult {
        // Get overlapping groups
        const overlappingGroupNames = error.overlappingNodes.map(node => node.groups).flat();
        const overlappingGroups = layout.groups.filter(group =>
            overlappingGroupNames.includes(group.name)
        );

        // Get relevant nodes, which are nodes in the overlapping groups
        const relevantNodeIds = overlappingGroups.flatMap(group => group.nodeIds)
        const relevantNodes = layout.nodes.filter(node => relevantNodeIds.includes(node.id));

        // Only edges containing overlapping nodes
        const edgesWithRelevantNodes = layout.edges.filter(edge =>
            relevantNodes.some(node => edge.source.id === node.id) && relevantNodes.some(node => edge.target.id === node.id)
        );
        
        const counterfactualLayout: InstanceLayout = {
            nodes: relevantNodes,
            edges: edgesWithRelevantNodes,
            constraints: layout.constraints,
            groups: overlappingGroups,
            overlappingNodes: error.overlappingNodes,
        }
        return { 
            layout: counterfactualLayout, 
            error: error,
            selectorErrors: this.selectorErrors
        };
    }

    /**
     * Applies the cyclic orientation constraints to the layout nodes.
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @throws {ConstraintError} If the layout cannot be satisfied with the cyclic constraints.
     * @returns An array of layout constraints.
     */
    /**
     * Builds disjunctive constraints for cyclic orientation constraints.
     * Each cyclic fragment generates a disjunction with N alternatives (perturbations),
     * where N is the number of nodes in the fragment.
     * 
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @returns Array of DisjunctiveConstraint instances, one per cyclic fragment.
     */
    private buildCyclicDisjunctions(layoutNodes: LayoutNode[], g: Graph): DisjunctiveConstraint[] {
        const cyclicConstraints = this._layoutSpec.constraints.orientation.cyclic;
        const disjunctions: DisjunctiveConstraint[] = [];

        // For each cyclic constraint, extract fragments
        for (const [, c] of cyclicConstraints.entries()) {
            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(c.selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(c.selector, 'cyclic orientation selector', error);
                continue; // Skip this cyclic constraint
            }
            if (!this.checkSelectorArity(selectorRes, c.selector, 'binary', 'cyclic orientation selector')) {
                continue; // Skip — unary selector in binary position
            }
            let selectedTuples: string[][] = selectorRes.selectedTwoples();
            let nextNodeMap: Map<LayoutNode, LayoutNode[]> = new Map<LayoutNode, LayoutNode[]>();
            
            // Build nextNodeMap from selected tuples
            selectedTuples.forEach((tuple) => {
                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[1];

                let srcN = layoutNodes.find((node) => node.id === sourceNodeId);
                let tgtN = layoutNodes.find((node) => node.id === targetNodeId);

                // Skip if either node is not found
                if (!srcN || !tgtN) {
                    return;
                }

                // Add an alignment edge so WebCola applies spring forces between
                // cyclic-constrained nodes, which is needed for positional constraints
                // to take effect when there is no existing graph edge between them.
                if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                    const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                    g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                }

                if (nextNodeMap.has(srcN)) {
                    nextNodeMap.get(srcN)!.push(tgtN);
                }
                else {
                    nextNodeMap.set(srcN, [tgtN]);
                }
            });

            let relatedNodeFragments = this.getFragmentsToConstrain(nextNodeMap);
            let relatedNodeIds = relatedNodeFragments.map((p) => p.Path.map((node) => node.id));

            // Apply counterclockwise reversal if needed
            if (c.direction === "counterclockwise") {
                relatedNodeIds = relatedNodeIds.map((fragment) => fragment.reverse());
            }

            // For each fragment, create a disjunction with N perturbations
            relatedNodeIds.forEach((fragment) => {
                const fragmentLength = fragment.length;
                
                // Fragments with 2 or fewer nodes don't need disjunctions
                if (fragmentLength <= 2) {
                    return;
                }

                // Generate all perturbations (rotations) of this fragment
                const alternatives: LayoutConstraint[][] = [];
                for (let perturbation = 0; perturbation < fragmentLength; perturbation++) {
                    const constraintsForPerturbation = this.getCyclicConstraintForFragment(
                        fragment,
                        layoutNodes,
                        perturbation,
                        c
                    );
                    alternatives.push(constraintsForPerturbation);
                }

                if (c.negated) {
                    // NOT(cyclic) via De Morgan: NOT(alt1 ∨ alt2 ∨ ...) = ¬alt1 ∧ ¬alt2 ∧ ...
                    // Build the positive disjunction, then negate it.
                    const positiveDisjunction = new DisjunctiveConstraint(c, alternatives);
                    const negatedDisjunctions = negateDisjunction(positiveDisjunction, c);
                    disjunctions.push(...negatedDisjunctions);
                } else {
                    // Create the disjunctive constraint
                    const disjunction = new DisjunctiveConstraint(c, alternatives);
                    disjunctions.push(disjunction);
                }
            });
        }

        return disjunctions;
    }

    private getCyclicConstraintForFragment(fragment: string[],
        layoutNodes: LayoutNode[],
        perturbationIdx: number,
        c: RelativeOrientationConstraint | CyclicOrientationConstraint | ImplicitConstraint): LayoutConstraint[] {
        const minRadius = 100;


        if (fragment.length <= 2) {
            return []; // No constraints needed for a two-node fragment.
        }

        const angleStep = (2 * Math.PI) / fragment.length;

        let fragmentNodePositions: Record<string, { x: number, y: number }> = {};

        for (var i = 0; i < fragment.length; i++) {
            let theta = (i + perturbationIdx) * angleStep;
            let x = minRadius * Math.cos(theta);
            let y = minRadius * Math.sin(theta);
            fragmentNodePositions[fragment[i]] = { x: x, y: y };
        }

        let fragmentConstraintsForCurrentOffset: LayoutConstraint[] = [];
        for (var k = 0; k < fragment.length; k++) {
            for (var j = 0; j < fragment.length; j++) {
                if (k !== j) {
                    let node1 = fragment[k];
                    let node2 = fragment[j];
                    let node1_pos = fragmentNodePositions[node1];
                    let node2_pos = fragmentNodePositions[node2];

                    if (node1_pos.x > node2_pos.x) {
                        fragmentConstraintsForCurrentOffset.push(this.leftConstraint(node2, node1, this.minSepWidth, layoutNodes, c));
                    }
                    else if (node1_pos.x < node2_pos.x) {
                        fragmentConstraintsForCurrentOffset.push(this.leftConstraint(node1, node2, this.minSepWidth, layoutNodes, c));
                    }
                    else {
                        // If they are on the same x-axis, we need to ensure that they are not on top of each other
                        fragmentConstraintsForCurrentOffset.push(this.ensureSameXConstraint(node1, node2, layoutNodes, c));
                    }

                    if (node1_pos.y > node2_pos.y) {
                        fragmentConstraintsForCurrentOffset.push(this.topConstraint(node2, node1, this.minSepHeight, layoutNodes, c));
                    }
                    else if (node1_pos.y < node2_pos.y) {
                        fragmentConstraintsForCurrentOffset.push(this.topConstraint(node1, node2, this.minSepHeight, layoutNodes, c));
                    }
                    else {
                        // If they are on the same y-axis, we need to ensure that they are not on top of each other
                        fragmentConstraintsForCurrentOffset.push(this.ensureSameYConstraint(node1, node2, layoutNodes, c));
                    }
                }
            }

        }

        return fragmentConstraintsForCurrentOffset;
    }




    private getAllPaths(nextNodeMap: Map<LayoutNode, LayoutNode[]>): LayoutNodePath[] {

        const allPaths: LayoutNodePath[] = [];

        const visited = new Set<LayoutNode>(); // To track visited nodes in the current path

        function dfs(currentNode: LayoutNode, path: LayoutNode[]): void {
            // Add the current node to the path
            path.push(currentNode);

            // If the current node has no outgoing edges, add the path to allPaths
            const neighbors = nextNodeMap.get(currentNode);
            if (!nextNodeMap.has(currentNode) || !neighbors || neighbors.length === 0) {

                let lnp = new LayoutNodePath(path, undefined);
                allPaths.push(lnp);
            } else {
                // Recursively visit all neighbors
                for (const neighbor of neighbors) {
                    if (!path.includes(neighbor)) {
                        // Continue DFS if the neighbor is not already in the path
                        dfs(neighbor, [...path]); // Pass a copy of the path to avoid mutation
                    } else {

                        let lnp = new LayoutNodePath(path, neighbor);
                        // If the neighbor is already in the path, we have a cycle
                        allPaths.push(lnp);
                    }
                }
            }
        }

        // Start DFS from each node in the map
        for (const startNode of nextNodeMap.keys()) {
            if (!visited.has(startNode)) { // Since we have already documented the paths from this node.
                dfs(startNode, []);
            }
        }

        return allPaths;
    }

    private getFragmentsToConstrain(nextNodeMap: Map<LayoutNode, LayoutNode[]>): LayoutNodePath[] {
        // Ensure allPaths are instances of the LayoutNodePath class
        const allPaths: LayoutNodePath[] = this.getAllPaths(nextNodeMap);

        // Step 1: Remove equivalent paths (keep only one representative per equivalence class)
        const nonEquivalentPaths: LayoutNodePath[] = allPaths.filter((p, i) => {
            return !allPaths.some((p2, j) => j < i && LayoutNodePath.areEquivalent(p, p2));
        });

        // Step 2: Remove paths that are strict subpaths of others
        const nonSubsumedPaths: LayoutNodePath[] = nonEquivalentPaths.filter((p, i) => {
            return !nonEquivalentPaths.some((p2, j) => i !== j && p2.isSubpathOf(p));
        });

        return nonSubsumedPaths;
    }



    /**
     * Applies the relative orientation constraints to the layout nodes.
     * Includes transitive reduction: if a < b and b < c exist, don't add a < c.
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @returns Conjunctive constraints and disjunctive constraints (from negation).
     */
    applyRelativeOrientationConstraints(layoutNodes: LayoutNode[], g: Graph): {
        conjunctive: LayoutConstraint[];
        disjunctive: DisjunctiveConstraint[];
    } {

        let constraints: LayoutConstraint[] = [];
        let disjunctions: DisjunctiveConstraint[] = [];
        let relativeOrientationConstraints = this._layoutSpec.constraints.orientation.relative;
        
        // Track generated constraints to avoid duplicates
        // Key format: "type:node1:node2:distance" (e.g., "left:a:b:15")
        const generatedConstraints = new Set<string>();
        
        // Track transitive relationships for left/right and top/bottom
        // Maps: node → set of nodes it's left of / above
        const leftOfGraph = new Map<string, Set<string>>();
        const aboveGraph = new Map<string, Set<string>>();

        relativeOrientationConstraints.forEach((c: RelativeOrientationConstraint) => {

            let directions = c.directions;
            let selector = c.selector;

            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(selector, 'orientation selector', error);
                return; // Skip this orientation constraint
            }
            if (!this.checkSelectorArity(selectorRes, selector, 'binary', 'orientation selector')) {
                return; // Skip — unary selector in binary position
            }
            let selectedTuples: string[][] = selectorRes.selectedTwoples();

            // For each tuple, we need to apply the constraints
            selectedTuples.forEach((tuple) => {
                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[1];

                // Skip tuples referencing hidden nodes; record the conflict for error reporting
                const sourceHidden = this.isNodeHiddenByDirective(sourceNodeId);
                const targetHidden = this.isNodeHiddenByDirective(targetNodeId);
                if (sourceHidden || targetHidden) {
                    const hiddenId = sourceHidden ? sourceNodeId : targetNodeId;
                    // Record one description per direction in natural language
                    for (const dir of directions) {
                        const description = describeDroppedOrientationConstraint(sourceNodeId, targetNodeId, dir);
                        this.recordHiddenNodeConflict(c, description, hiddenId);
                    }
                    return; // Skip this tuple
                }

                // For negated multi-direction constraints, De Morgan applies:
                // NOT(dir1 AND dir2) = NOT(dir1) OR NOT(dir2)
                // We collect all negated alternatives across directions for this tuple
                // and wrap them in a single DisjunctiveConstraint.
                if (c.negated) {
                    // Add alignment edge
                    if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                        const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                        g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                    }

                    if (directions.length === 1) {
                        // Single direction: negation is a single conjunctive constraint
                        // (or a disjunction for directly* variants)
                        const negated = this.negateOrientationDirection(
                            directions[0], sourceNodeId, targetNodeId, layoutNodes, c
                        );
                        if (negated.length === 1) {
                            // Single flipped constraint — add conjunctively
                            constraints.push(negated[0][0]);
                        } else {
                            // Disjunction (directly* variants produce NOT(order) OR NOT(align))
                            disjunctions.push(new DisjunctiveConstraint(c, negated));
                        }
                    } else {
                        // Multiple directions: NOT(d1 AND d2 AND ...) = NOT(d1) OR NOT(d2) OR ...
                        // Each negated direction may itself produce multiple alternatives
                        // (e.g., directly* variants), so we flatten them all into one disjunction.
                        const allAlternatives: LayoutConstraint[][] = [];
                        for (const direction of directions) {
                            const negated = this.negateOrientationDirection(
                                direction, sourceNodeId, targetNodeId, layoutNodes, c
                            );
                            allAlternatives.push(...negated);
                        }
                        disjunctions.push(new DisjunctiveConstraint(c, allAlternatives));
                    }
                    return; // Skip positive constraint logic for this tuple
                }

                directions.forEach((direction) => {
                    // Add alignment edge for ALL orientation constraints if enabled AND edge doesn't already exist in the graph
                    if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                        const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                        g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                    }

                    if (direction == "left") {
                        const key = `left:${targetNodeId}:${sourceNodeId}:${this.minSepWidth}`;
                        // Check if transitively implied: is there a path targetNodeId -> ... -> sourceNodeId?
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(leftOfGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(leftOfGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.leftConstraint(targetNodeId, sourceNodeId, this.minSepWidth, layoutNodes, c));
                        }
                    }
                    else if (direction == "above") {
                        const key = `top:${targetNodeId}:${sourceNodeId}:${this.minSepHeight}`;
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(aboveGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(aboveGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.topConstraint(targetNodeId, sourceNodeId, this.minSepHeight, layoutNodes, c));
                        }
                    }
                    else if (direction == "right") {
                        const key = `left:${sourceNodeId}:${targetNodeId}:${this.minSepWidth}`;
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(leftOfGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(leftOfGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.leftConstraint(sourceNodeId, targetNodeId, this.minSepWidth, layoutNodes, c));
                        }
                    }
                    else if (direction == "below") {
                        const key = `top:${sourceNodeId}:${targetNodeId}:${this.minSepHeight}`;
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(aboveGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(aboveGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.topConstraint(sourceNodeId, targetNodeId, this.minSepHeight, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyLeft") {
                        const leftKey = `left:${targetNodeId}:${sourceNodeId}:${this.minSepWidth}`;
                        const alignKey = `align-y:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(leftKey) && !this.hasTransitivePath(leftOfGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(leftKey);
                            this.addToTransitiveGraph(leftOfGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.leftConstraint(targetNodeId, sourceNodeId, this.minSepWidth, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameYConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyAbove") {
                        const topKey = `top:${targetNodeId}:${sourceNodeId}:${this.minSepHeight}`;
                        const alignKey = `align-x:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(topKey) && !this.hasTransitivePath(aboveGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(topKey);
                            this.addToTransitiveGraph(aboveGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.topConstraint(targetNodeId, sourceNodeId, this.minSepHeight, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameXConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyRight") {
                        const leftKey = `left:${sourceNodeId}:${targetNodeId}:${this.minSepWidth}`;
                        const alignKey = `align-y:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(leftKey) && !this.hasTransitivePath(leftOfGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(leftKey);
                            this.addToTransitiveGraph(leftOfGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.leftConstraint(sourceNodeId, targetNodeId, this.minSepWidth, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameYConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyBelow") {
                        const topKey = `top:${sourceNodeId}:${targetNodeId}:${this.minSepHeight}`;
                        const alignKey = `align-x:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(topKey) && !this.hasTransitivePath(aboveGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(topKey);
                            this.addToTransitiveGraph(aboveGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.topConstraint(sourceNodeId, targetNodeId, this.minSepHeight, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameXConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                });
            });
        });

        return { conjunctive: constraints, disjunctive: disjunctions };
    }

    /**
     * Produces the negated constraint alternatives for a single orientation direction on a tuple.
     *
     * Returns LayoutConstraint[][] — an array of alternatives for a DisjunctiveConstraint.
     *
     * For cardinal directions (above/below/left/right):
     *   NOT(ordering) = single flipped constraint → [[flipped]]
     *
     * For "directly" variants (directlyAbove = ordering AND alignment):
     *   ¬(ordering ∧ alignment) = ¬ordering ∨ ¬alignment
     *   ¬ordering = (reverse ∨ align) → 2 alternatives
     *   ¬alignment = (differ-alt1 ∨ differ-alt2) → 2 alternatives
     *   Total: 4 alternatives.
     */
    private negateOrientationDirection(
        direction: RelativeDirection,
        sourceNodeId: string, targetNodeId: string,
        layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource
    ): LayoutConstraint[][] {
        // Cardinal directions: ¬(A < B) = (B < A) ∨ (A ≡ B)  →  2-alternative disjunction
        switch (direction) {
            case "left":
                return [
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];
            case "right":
                return [
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];
            case "above":
                return [
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];
            case "below":
                return [
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];

            // directly* variants: ¬(ordering ∧ alignment) = ¬ordering ∨ ¬alignment
            // ¬ordering = 2 alternatives (reverse ∨ align), ¬alignment = 2 alternatives → 4 total
            case "directlyLeft":
                // Positive: target < source on x ∧ same Y
                return [
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            case "directlyRight":
                // Positive: source < target on x ∧ same Y
                return [
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            case "directlyAbove":
                // Positive: target < source on y ∧ same X
                return [
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            case "directlyBelow":
                // Positive: source < target on y ∧ same X
                return [
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            default:
                return [];
        }
    }

    /**
     * Checks if there's a transitive path from source to target in the graph.
     * Used to detect redundant constraints via transitivity.
     * @param graph - Map of node to its reachable nodes
     * @param source - Source node
     * @param target - Target node
     * @returns True if path exists (BFS)
     */
    private hasTransitivePath(graph: Map<string, Set<string>>, source: string, target: string): boolean {
        const sourceReachable = graph.get(source);
        if (!sourceReachable) return false;
        
        // Direct connection
        if (sourceReachable.has(target)) return true;
        
        // BFS to find transitive path
        const visited = new Set<string>([source]);
        const queue = Array.from(sourceReachable);
        
        while (queue.length > 0) {
            const current = queue.shift()!;
            if (current === target) return true;
            if (visited.has(current)) continue;
            visited.add(current);
            
            const neighbors = graph.get(current);
            if (neighbors) {
                for (const neighbor of neighbors) {
                    if (!visited.has(neighbor)) {
                        queue.push(neighbor);
                    }
                }
            }
        }
        
        return false;
    }
    
    /**
     * Adds an edge to the transitive graph and updates transitive closure incrementally.
     * @param graph - Map of node to its reachable nodes
     * @param from - Source node
     * @param to - Target node
     */
    private addToTransitiveGraph(graph: Map<string, Set<string>>, from: string, to: string): void {
        // Add direct edge
        if (!graph.has(from)) {
            graph.set(from, new Set());
        }
        graph.get(from)!.add(to);
        
        // Update transitive closure: all nodes that reach 'from' can now also reach anything 'to' can reach
        const toReachable = graph.get(to) || new Set<string>();
        
        // Find all nodes that can reach 'from'
        for (const [node, reachable] of graph.entries()) {
            if (node === from || reachable.has(from)) {
                // This node can reach 'from', so it can also reach 'to' and everything 'to' reaches
                reachable.add(to);
                for (const transitiveTarget of toReachable) {
                    reachable.add(transitiveTarget);
                }
            }
        }
    }

    /**
     * Applies the align constraints to the layout nodes.
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @returns An array of layout constraints.
     */
    applyAlignConstraints(layoutNodes: LayoutNode[], g: Graph): {
        conjunctive: LayoutConstraint[];
        disjunctive: DisjunctiveConstraint[];
    } {
        let constraints: LayoutConstraint[] = [];
        let disjunctions: DisjunctiveConstraint[] = [];
        let alignConstraints = this._layoutSpec.constraints.alignment;

        // Track generated alignment constraints to avoid duplicates
        // Use normalized key (sorted node IDs) since alignment is symmetric
        const generatedAlignments = new Set<string>();

        alignConstraints.forEach((c: AlignConstraint) => {
            let direction = c.direction;
            let selector = c.selector;

            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(selector, 'align selector', error);
                return; // Skip this align constraint
            }
            if (!this.checkSelectorArity(selectorRes, selector, 'binary', 'align selector')) {
                return; // Skip — unary selector in binary position
            }
            let selectedTuples: string[][] = selectorRes.selectedTwoples();

            // For each tuple, apply the alignment constraint
            selectedTuples.forEach((tuple) => {
                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[1];

                // Skip tuples referencing hidden nodes; record the conflict for error reporting
                const sourceHidden = this.isNodeHiddenByDirective(sourceNodeId);
                const targetHidden = this.isNodeHiddenByDirective(targetNodeId);
                if (sourceHidden || targetHidden) {
                    const hiddenId = sourceHidden ? sourceNodeId : targetNodeId;
                    const alignDir = direction === 'horizontal' ? 'horizontally' : 'vertically';
                    const description = `${sourceNodeId} is ${alignDir} aligned with ${targetNodeId}`;
                    this.recordHiddenNodeConflict(c, description, hiddenId);
                    return; // Skip this tuple
                }

                // Add alignment edge for align constraints if enabled AND edge doesn't already exist in the graph
                if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                    const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                    g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                }

                // For negated alignment: NOT(same-Y) = must differ on Y = above OR below (disjunction)
                if (c.negated) {
                    const [node1, node2] = [sourceNodeId, targetNodeId].sort();
                    const key = `not-align-${direction === 'horizontal' ? 'y' : 'x'}:${node1}:${node2}`;
                    if (!generatedAlignments.has(key)) {
                        generatedAlignments.add(key);
                        if (direction === "horizontal") {
                            // NOT same-Y → source above target OR target above source
                            const alt1 = [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, c)];
                            const alt2 = [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, c)];
                            disjunctions.push(new DisjunctiveConstraint(c, [alt1, alt2]));
                        } else {
                            // NOT same-X → source left of target OR target left of source
                            const alt1 = [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, c)];
                            const alt2 = [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, c)];
                            disjunctions.push(new DisjunctiveConstraint(c, [alt1, alt2]));
                        }
                    }
                    return; // Skip positive alignment logic
                }

                if (direction === "horizontal") {
                    // Horizontal alignment means same Y coordinate
                    // Normalize node order for key (alignment is symmetric)
                    const [node1, node2] = [sourceNodeId, targetNodeId].sort();
                    const key = `align-y:${node1}:${node2}`;
                    if (!generatedAlignments.has(key)) {
                        generatedAlignments.add(key);
                        constraints.push(this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, c));
                    }
                } else if (direction === "vertical") {
                    // Vertical alignment means same X coordinate
                    const [node1, node2] = [sourceNodeId, targetNodeId].sort();
                    const key = `align-x:${node1}:${node2}`;
                    if (!generatedAlignments.has(key)) {
                        generatedAlignments.add(key);
                        constraints.push(this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, c));
                    }
                }
            });
        });

        return { conjunctive: constraints, disjunctive: disjunctions };
    }




    /**
     * Checks if there's already a direct edge (bidirectional) between two nodes in the graph.
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @returns true if there's already an edge between the nodes
     */
    /**
     * Checks if two nodes should have an alignment edge added based on the current strategy.
     * 
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @returns true if an alignment edge should be added (nodes are not connected according to strategy)
     */
    private shouldAddAlignmentEdge(g: Graph, sourceNodeId: string, targetNodeId: string): boolean {
        // If strategy is NEVER, never add alignment edges
        if (this.alignmentEdgeStrategy === AlignmentEdgeStrategy.NEVER) {
            return false;
        }

        // Check for direct edge
        const hasDirectEdge = this.hasDirectEdgeBetween(g, sourceNodeId, targetNodeId);
        
        // If strategy is DIRECT, only check for direct edges
        if (this.alignmentEdgeStrategy === AlignmentEdgeStrategy.DIRECT) {
            return !hasDirectEdge;
        }

        // Strategy is CONNECTED: check if nodes are connected via any path
        // If they have a direct edge, they're connected
        if (hasDirectEdge) {
            return false;
        }

        // Check if connected via any path (including alignment edges)
        return !this.isConnectedViaPath(g, sourceNodeId, targetNodeId);
    }

    /**
     * Checks if there's a direct edge between two nodes (either direction).
     * 
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @returns true if there's a direct edge between the nodes
     */
    private hasDirectEdgeBetween(g: Graph, sourceNodeId: string, targetNodeId: string): boolean {
        // Direct edge check (either direction). Prefer graphlib.hasEdge if available.
        return (typeof (g as any).hasEdge === 'function' && ((g as any).hasEdge(sourceNodeId, targetNodeId) || (g as any).hasEdge(targetNodeId, sourceNodeId)))
            ||
            // fallback to scanning in/out edges (handles multi-edges)
            ((g.inEdges(sourceNodeId) || []).some(e => e.v === targetNodeId) ||
             (g.outEdges(sourceNodeId) || []).some(e => e.w === targetNodeId) ||
             (g.inEdges(targetNodeId) || []).some(e => e.v === sourceNodeId) ||
             (g.outEdges(targetNodeId) || []).some(e => e.w === sourceNodeId));
    }

    /**
     * Checks if two nodes are connected via any path in the graph.
     * This is used to determine whether to add alignment edges for WebCola.
     * 
     * Performance optimization: We skip alignment edges when nodes are already
     * connected via any path (not just directly connected). This significantly
     * reduces the number of constraints for large graphs while maintaining good
     * layout quality, as connected nodes are less likely to fall into bad local minima.
     * 
     * Note: This follows ALL edges including alignment edges (edges with _alignment_ prefix).
     * This ensures we don't add redundant alignment edges when nodes are already connected
     * via other alignment edges.
     * 
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @param excludeEdge - Optional edge to exclude from the connectivity check (for pruning)
     * @returns true if there's any path between the nodes (treating graph as undirected)
     */
    private isConnectedViaPath(g: Graph, sourceNodeId: string, targetNodeId: string, excludeEdge?: { v: string, w: string, name?: string }): boolean {
        // BFS treating graph as undirected (follow predecessors and successors)
        const visited = new Set<string>();
        const queue: string[] = [sourceNodeId];

        while (queue.length > 0) {
            const cur = queue.shift()!;
            if (cur === targetNodeId) {
                return true; // Connected via path
            }
            if (visited.has(cur)) continue;
            visited.add(cur);

            // Get all edges from current node
            const outEdges = g.outEdges(cur) || [];
            const inEdges = g.inEdges(cur) || [];
            
            // Process all edges, excluding the specified edge if provided
            for (const edge of [...outEdges, ...inEdges]) {
                // Skip the excluded edge if specified
                if (excludeEdge && 
                    ((edge.v === excludeEdge.v && edge.w === excludeEdge.w && edge.name === excludeEdge.name) ||
                     (edge.v === excludeEdge.w && edge.w === excludeEdge.v && edge.name === excludeEdge.name))) {
                    continue;
                }
                
                // Add the neighbor to the queue
                const neighbor = edge.v === cur ? edge.w : edge.v;
                if (!visited.has(neighbor)) {
                    queue.push(neighbor);
                }
            }
        }

        return false; // Not connected
    }

    /**
     * Prunes redundant alignment edges from the graph after all alignment edges have been added.
     * An alignment edge is redundant if removing it doesn't disconnect the nodes it connects.
     * 
     * This is useful for the case where alignment constraints create cycles or multiple paths,
     * and some edges can be removed without affecting connectivity.
     * 
     * Example: If a→b→c→a forms a cycle of alignment edges, one edge can be removed.
     * 
     * @param g - The graph containing alignment edges
     */
    private pruneRedundantAlignmentEdges(g: Graph): void {
        // Only prune if strategy is CONNECTED (otherwise we might break the intended behavior)
        if (this.alignmentEdgeStrategy !== AlignmentEdgeStrategy.CONNECTED) {
            return;
        }

        // Collect all alignment edges
        const alignmentEdges = g.edges().filter(edge => {
            const edgeId = edge.name;
            return edgeId && edgeId.includes('_alignment_');
        });

        // Track which edges we've removed
        const removedEdges: typeof alignmentEdges = [];

        // Try to remove each alignment edge
        for (const edge of alignmentEdges) {
            // Check if nodes are still connected without this edge
            if (this.isConnectedViaPath(g, edge.v, edge.w, edge)) {
                // Nodes are still connected, so this edge is redundant
                g.removeEdge(edge.v, edge.w, edge.name);
                removedEdges.push(edge);
            }
        }

        // Log pruning statistics if any edges were removed
        if (removedEdges.length > 0) {
            console.log(`Pruned ${removedEdges.length} redundant alignment edges out of ${alignmentEdges.length} total alignment edges`);
        }
    }

    private getDisconnectedNodes(g: Graph): string[] {
        let inNodes = g.edges().map(edge => edge.w);
        let outNodes = g.edges().map(edge => edge.v);

        // All nodes in the graph
        let allNodes = new Set(g.nodes());
        let allConnectedNodes = new Set([...inNodes, ...outNodes]);
        let disconnectedNodes = [...allNodes].filter(node => !allConnectedNodes.has(node));
        return disconnectedNodes;
    }

    private buildLayoutEdges(g: Graph, layoutNodes: LayoutNode[]): LayoutEdge[] {
        return g.edges().map((edge) => {

            const edgeId = edge.name;
            const edgeLabel: string = g.edge(edge.v, edge.w, edgeId);
            let source = layoutNodes.find((node) => node.id === edge.v);
            let target = layoutNodes.find((node) => node.id === edge.w);
            let relName = this.getRelationName(g, edge);
            let color = this.getEdgeColor(relName, edge.v, edge.w, edgeId);
            let style = this.getEdgeStyle(relName, edge.v, edge.w, edgeId);
            let weight = this.getEdgeWeight(relName, edge.v, edge.w, edgeId);
            let showLabel = this.getEdgeShowLabel(relName, edge.v, edge.w, edgeId);
            let highlight = this.getEdgeHighlight(relName, edge.v, edge.w, edgeId);

            // Skip edges with missing source or target nodes
            if (!source || !target || !edgeId) {
                return null;
            }

            let e: LayoutEdge = {
                source: source,
                target: target,
                label: edgeLabel,
                relationName: relName,
                id: edgeId,
                color: color,
                style: style,
                weight: weight,
                showLabel: showLabel,
                highlight: highlight,
                // For group edges the graphlib edge label IS the group name (see constructGroupEdgeID).
                // Carry it forward so the renderer can look up the group directly by ID
                // without re-parsing the edge ID string or matching fragile leaf indices.
                groupId: edgeId.startsWith('_g_') ? edgeLabel : undefined,
                // edge.v is always the groupOn (key/anchor) node — stamp it so the renderer
                // knows definitively which end is the anchor vs. the group member.
                keyNodeId: edgeId.startsWith('_g_') ? edge.v : undefined,
            };
            return e;
        }).filter((edge): edge is LayoutEdge => edge !== null);
    }

    private filterHiddenEdges(layoutEdges: LayoutEdge[]): LayoutEdge[] {
        return layoutEdges.filter((edge) => !edge.id.startsWith(this.hideThisEdge));
    }



    private getNodeFromId(nodeId: string, layoutNodes: LayoutNode[], sourceConstraint?: ConstraintSource): LayoutNode {
        let node = layoutNodes.find((node) => node.id === nodeId);
        if (!node) {
            throw new MissingNodeConstraintError(nodeId, sourceConstraint);
        }
        return node;
    }


    private leftConstraint(leftId: string, rightId: string, minDistance: number, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): LeftConstraint {

        let left = this.getNodeFromId(leftId, layoutNodes, sourceConstraint);
        let right = this.getNodeFromId(rightId, layoutNodes, sourceConstraint);
        return { left: left, right: right, minDistance: minDistance, sourceConstraint: sourceConstraint };
    }

    private topConstraint(topId: string, bottomId: string, minDistance: number, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): TopConstraint {

        let top = this.getNodeFromId(topId, layoutNodes, sourceConstraint);
        let bottom = this.getNodeFromId(bottomId, layoutNodes, sourceConstraint);

        return { top: top, bottom: bottom, minDistance: minDistance, sourceConstraint: sourceConstraint };
    }

    private ensureSameYConstraint(node1Id: string, node2Id: string, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): AlignmentConstraint {

        let node1 = this.getNodeFromId(node1Id, layoutNodes, sourceConstraint);
        let node2 = this.getNodeFromId(node2Id, layoutNodes, sourceConstraint);

        return { axis: "y", node1: node1, node2: node2, sourceConstraint: sourceConstraint };
    }

    private ensureSameXConstraint(node1Id: string, node2Id: string, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): AlignmentConstraint {

        let node1 = this.getNodeFromId(node1Id, layoutNodes, sourceConstraint);
        let node2 = this.getNodeFromId(node2Id, layoutNodes, sourceConstraint);

        return { axis: "x", node1: node1, node2: node2, sourceConstraint: sourceConstraint };
    }

    private singletonGroup(nodeId: string): LayoutGroup {

        let groupName = `${LayoutInstance.DISCONNECTED_PREFIX}${nodeId}`;

        return {
            name: groupName,
            nodeIds: [nodeId],
            keyNodeId: nodeId,
            showLabel: false
        }
    }



    /**
     * Compute, for every node in `g`, the strings that the renderer will draw
     * inside its box: main label, attribute lines, and Skolem-label lines.
     * These are the inputs to `estimateLabelBox` so the auto-sized default
     * matches what the user will actually see.
     *
     * Line formatting mirrors `webcola-cnd-graph.ts`:
     *   - main label = `g.node(nodeId).label || nodeId`
     *   - each attribute key produces one line: `"key: v1, v2, ..."`
     *   - each Skolem-label key produces one line: `"v1, v2, ..."` (key omitted)
     */
    private getNodeDisplayContent(
        g: Graph,
        ai: IDataInstance,
        attributes: Record<string, Record<string, string[]>>
    ): Record<string, NodeDisplayContent> {
        const result: Record<string, NodeDisplayContent> = {};
        const atoms = ai.getAtoms();

        for (const nodeId of g.nodes()) {
            const nodeMetadata = g.node(nodeId);
            const label = nodeMetadata?.label || nodeId;

            const nodeAttrs = attributes[nodeId] || {};
            const attributeLines = Object.entries(nodeAttrs)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, values]) => `${key}: ${values.join(', ')}`);

            const atom = atoms.find((x) => x.id === nodeId);
            const skolemLines: string[] = [];
            if (atom?.labels) {
                for (const values of Object.values(atom.labels)) {
                    skolemLines.push(Array.isArray(values) ? values.join(', ') : String(values));
                }
            }

            result[nodeId] = { label, attributeLines, skolemLines };
        }
        return result;
    }

    private getNodeSizeMap(
        g: Graph,
        contentByNode: Record<string, NodeDisplayContent>
    ): Record<string, { width: number; height: number }> {
        let nodeSizeMap: Record<string, { width: number; height: number }> = {};

        // Apply size directives first. These come from `size` constraints in
        // the layout spec and must produce exactly the requested dimensions
        // (matching `sat_size` in the Lean mechanization: ∀ a ∈ S, b.width = w
        // ∧ b.height = h).
        let sizeDirectives = this._layoutSpec.directives.sizes;
        sizeDirectives.forEach((sizeDirective) => {
            let selectedNodes: string[];
            try {
                const selectorRes = this.evaluator.evaluate(sizeDirective.selector, { instanceIndex: this.instanceNum });
                if (!this.checkSelectorArity(selectorRes, sizeDirective.selector, 'unary', 'size selector')) {
                    return; // Skip — binary selector in unary position
                }
                selectedNodes = selectorRes.selectedAtoms();
            } catch (error) {
                this.recordSelectorError(sizeDirective.selector, 'size selector', error);
                return; // Skip this size directive
            }
            let width = sizeDirective.width;
            let height = sizeDirective.height;

            selectedNodes.forEach((nodeId) => {

                if (nodeSizeMap[nodeId]) {

                    const existingSize = nodeSizeMap[nodeId];
                    if (existingSize.width !== width || existingSize.height !== height) {
                        throw new Error(
                            `Size Conflict: "${nodeId}" cannot have multiple sizes: ${JSON.stringify(existingSize)}, ${JSON.stringify({ width, height })}.`
                        );
                    }
                }

                nodeSizeMap[nodeId] = { width: width, height: height };
            });
        });

        // Auto-size nodes that no `size` directive matched. The estimator
        // floor is DEFAULT_NODE_WIDTH × DEFAULT_NODE_HEIGHT (100 × 60), so
        // short-label nodes keep their historical footprint and only
        // long-label nodes grow.
        const floor = { w: this.DEFAULT_NODE_WIDTH, h: this.DEFAULT_NODE_HEIGHT };
        for (const nodeId of g.nodes()) {
            if (nodeSizeMap[nodeId]) continue;
            const c = contentByNode[nodeId];
            const main = c?.label ?? '';
            const secondary = c ? [...c.attributeLines, ...c.skolemLines] : [];
            nodeSizeMap[nodeId] = estimateLabelBox(main, secondary, { min: floor });
        }

        return nodeSizeMap;
    }


    private getNodeColorMap(g: Graph, a: IDataInstance): Record<string, string> {
        let nodeColorMap: Record<string, string> = {};

        // Start by getting the default signature colors
        let sigColors = this.getSigColors(a);

        // Apply color directives first
        let colorDirectives = this._layoutSpec.directives.atomColors;
        colorDirectives.forEach((colorDirective) => {
            let selected: string[];
            try {
                const selectorRes = this.evaluator.evaluate(colorDirective.selector, { instanceIndex: this.instanceNum });
                if (!this.checkSelectorArity(selectorRes, colorDirective.selector, 'unary', 'color selector')) {
                    return; // Skip — binary selector in unary position
                }
                selected = selectorRes.selectedAtoms();
            } catch (error) {
                this.recordSelectorError(colorDirective.selector, 'color selector', error);
                return; // Skip this color directive
            }
            let color = colorDirective.color;

            selected.forEach((nodeId) => {
                if (nodeColorMap[nodeId]) {
                    const existingColor = nodeColorMap[nodeId];
                    if (existingColor !== color) {
                        throw new Error(
                            `Color Conflict: "${nodeId}" cannot have multiple colors: ${existingColor}, ${color}.`
                        );
                    }
                }
                nodeColorMap[nodeId] = color;
            });
        });

        // Set default colors for nodes that do not have a color set
        let graphNodes = [...g.nodes()];
        graphNodes.forEach((nodeId) => {
            if (!nodeColorMap[nodeId]) {
                let mostSpecificType = this.getMostSpecificType(nodeId, a);
                nodeColorMap[nodeId] = sigColors[mostSpecificType];
            }
        });

        return nodeColorMap;
    }

    private getNodeIconMap(g: Graph): Record<string, { path: string, showLabels: boolean }> {
        let nodeIconMap: Record<string, { path: string, showLabels: boolean }> = {};
        const DEFAULT_ICON = this.DEFAULT_NODE_ICON_PATH;

        // Apply icon directives first
        let iconDirectives = this._layoutSpec.directives.icons;
        iconDirectives.forEach((iconDirective) => {
            let selected: string[];
            try {
                const selectorRes = this.evaluator.evaluate(iconDirective.selector, { instanceIndex: this.instanceNum });
                if (!this.checkSelectorArity(selectorRes, iconDirective.selector, 'unary', 'icon selector')) {
                    return; // Skip — binary selector in unary position
                }
                selected = selectorRes.selectedAtoms();
            } catch (error) {
                this.recordSelectorError(iconDirective.selector, 'icon selector', error);
                return; // Skip this icon directive
            }
            let iconPath = iconDirective.path;

            selected.forEach((nodeId) => {
                // Resolve icon path (handles bundled icons, icon packs, and URLs)
                const resolvedPath = resolveIconPath(iconPath);
                if (nodeIconMap[nodeId]) {
                    const existingIcon = nodeIconMap[nodeId];
                    if (existingIcon.path !== resolvedPath || existingIcon.showLabels !== iconDirective.showLabels) {
                        throw new Error(
                            `Icon Conflict: "${nodeId}" cannot have multiple icons: ${JSON.stringify(existingIcon)}, ${JSON.stringify({ path: resolvedPath, showLabels: iconDirective.showLabels })}.`
                        );
                    }
                }
                nodeIconMap[nodeId] = { path: resolvedPath, showLabels: iconDirective.showLabels };
            });
        });

        // Set default icons for nodes that do not have an icon set
        let graphNodes = [...g.nodes()];
        graphNodes.forEach((nodeId) => {
            if (!nodeIconMap[nodeId]) {
                nodeIconMap[nodeId] = { path: DEFAULT_ICON, showLabels: true };
            }
        });

        return nodeIconMap;
    }

    /**
     * Gets the color for a specific edge based on directives.
     * @param relName - The relation name of the edge.
     * @param sourceAtom - The source atom ID.
     * @param targetAtom - The target atom ID.
     * @param edgeId - The edge ID (optional, used to identify inferred edges).
     * @returns The color for the edge, or "black" as default.
     */
    private getEdgeColor(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): string {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        if (inferredDirective?.color) {
            return inferredDirective.color;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        if (directive?.color) {
            return directive.color;
        }

        return "black"; // Default color
    }

    private getEdgeStyle(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): EdgeStyle | undefined {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        const inferredStyle = normalizeEdgeStyle(inferredDirective?.style);
        if (inferredStyle) {
            return inferredStyle;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return normalizeEdgeStyle(directive?.style);
    }

    private getEdgeHighlight(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): string | undefined {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        if (inferredDirective?.highlight) {
            return inferredDirective.highlight;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return directive?.highlight;
    }

    private getEdgeWeight(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): number | undefined {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        const inferredWeight = this.normalizeEdgeWeight(inferredDirective?.weight, "inferred edge");
        if (inferredWeight !== undefined) {
            return inferredWeight;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return this.normalizeEdgeWeight(directive?.weight, "edge");
    }

    /**
     * Gets whether the label should be shown for a specific edge based on directives.
     * @param relName - The relation name of the edge.
     * @param sourceAtom - The source atom ID.
     * @param targetAtom - The target atom ID.
     * @param edgeId - The edge ID (optional, used to identify inferred edges).
     * @returns true if the label should be shown, false if hidden, undefined to use default.
     *          Note: Inferred edges always show labels, so this only applies to regular edges.
     */
    private getEdgeShowLabel(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): boolean | undefined {
        // Inferred edges always show labels - no showLabel check needed
        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return directive?.showLabel;
    }

    private getInferredEdgeDirective(edgeId?: string): InferredEdgeDirective | undefined {
        const inferredEdgePrefix = "_inferred_";
        if (!edgeId || !edgeId.includes(inferredEdgePrefix)) {
            return undefined;
        }

        const inferredEdges = this._layoutSpec.directives.inferredEdges;
        return inferredEdges.find((directive) => edgeId.includes(`${inferredEdgePrefix}<:${directive.name}`));
    }

    private findEdgeDirective(relName: string, sourceAtom: string, targetAtom?: string): EdgeColorDirective | undefined {
        const colorDirectives = this._layoutSpec.directives.edgeColors;
        
        for (const directive of colorDirectives) {
            if (directive.field !== relName) {
                continue;
            }
            
            // Check if selector matches (or no selector means match all sources)
            let selectorMatches = true;
            if (directive.selector) {
                try {
                    const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                    const selectedAtoms = selectorResult.selectedAtoms();
                    selectorMatches = selectedAtoms.includes(sourceAtom);
                } catch (error) {
                    this.recordSelectorError(directive.selector, 'edge selector', error);
                    selectorMatches = false;
                }
            }
            
            if (!selectorMatches) {
                continue;
            }
            
            // Check if filter matches (or no filter means match all tuples)
            let filterMatches = true;
            if (directive.filter && targetAtom) {
                try {
                    const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                    const selectedTuples = filterResult.selectedTwoples();
                    // Check if the (source, target) pair is in the filtered set
                    filterMatches = selectedTuples.some(
                        tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                    );
                } catch (error) {
                    this.recordSelectorError(directive.filter, 'edge filter', error);
                    filterMatches = false;
                }
            }
            
            if (selectorMatches && filterMatches) {
                return directive;
            }
        }

        return undefined;
    }

    private normalizeEdgeWeight(weight: unknown, context: string): number | undefined {
        if (weight === undefined || weight === null) {
            return undefined;
        }

        if (typeof weight !== "number" || Number.isNaN(weight) || weight <= 0) {
            console.warn(`Ignoring ${context} weight because it is not a positive number: ${weight}`);
            return undefined;
        }

        return weight;
    }

    /**
     * Obtains the default sig colors for each sig type.
     * @param ai - The Alloy instance to get the sig colors for.
     * @returns a Record mapping sig types to their colors.
     */
    private getSigColors(ai: IDataInstance): Record<string, string> {
        let sigColors: Record<string, string> = {};

        let types = ai.getTypes();
        let colorPicker = new ColorPicker(types.length);
        types.forEach((type) => {
            sigColors[type.id] = colorPicker.getNextColor();
        });
        return sigColors;
    }

    private getFieldTuples(a: IDataInstance, fieldName: string): string[][] {

        let relations = a.getRelations();
        let vals = Object.values(relations);
        let field = Object.values(relations).find((rel) => rel.name === fieldName);


        if (!field) {
            return [];
        }

        let fieldTuples = field.tuples.map((tuple) => {
            return tuple.atoms;
        });
        return fieldTuples;
    }

    private getFieldTuplesForSourceAndTarget(a: IDataInstance, fieldName: string, src: string, tgt: string): string[][] {

        let fieldTuples = this.getFieldTuples(a, fieldName);
        let filteredTuples = fieldTuples.filter((tuple) => {
            let arity = tuple.length;
            if (arity < 1) {
                return false;
            }
            return tuple[0] === src && tuple[arity - 1] === tgt;
        });

        return filteredTuples;

    }

    // g is an inout parameter. I.E. it will be modified.
    private addinferredEdges(g: Graph) {

        const inferredEdgePrefix = "_inferred_";
        let inferredEdges = this._layoutSpec.directives.inferredEdges;
        inferredEdges.forEach((he) => {

            let res;
            try {
                res = this.evaluator.evaluate(he.selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(he.selector, 'inferredEdge selector', error);
                return; // Skip this inferred edge
            }

            let selectedTuples: string[][] = res.selectedTuplesAll();
            let edgeIdPrefix = `${inferredEdgePrefix}<:${he.name}`;

            selectedTuples.forEach((tuple) => {

                let n = tuple.length;

                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[n - 1];

               let edgeLabel = he.name;

                // Use node labels for middle nodes instead of IDs
                if (n > 2) {
                    // Get labels for middle nodes
                    let middleNodeLabels = tuple.slice(1, n - 1).map(nodeId => {
                        const nodeMetadata = g.node(nodeId);
                        return nodeMetadata?.label || nodeId; // Use label if available, fallback to ID
                    }).join(",");
                    edgeLabel = `${edgeLabel}[${middleNodeLabels}]`;
                }
                // The edge 

                let fullTuple = tuple.join("->");

                let edgeId = `${edgeIdPrefix}<:${fullTuple}`;
                g.setEdge(sourceNodeId, targetNodeId, edgeLabel, edgeId);
            });
        });
    }
}
